import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";

import {
  fetchMeetingMinutes,
  deleteMeetingMinutes,
} from "../../../api/meetingMinutes";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";

export const MeetingMinutesList = () => {
  const { t } = useTranslation();
  const [meetingMinutes, setMeetingMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        query: searchQuery,
        per_page: 50,
        page: 1,
      };
      const response = await fetchMeetingMinutes(params);
      setMeetingMinutes(response.data || []);
    } catch (err) {
      console.error("Error fetching meeting minutes:", err);
      setError(true);
      toast.error(t("meeting_minutes.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("meeting_minutes.delete_confirm"))) {
      return;
    }

    try {
      await deleteMeetingMinutes(id);
      toast.success(t("meeting_minutes.deleted"));
      fetchData();
    } catch (err) {
      console.error("Error deleting meeting minutes:", err);
      toast.error(t("meeting_minutes.delete_error"));
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (error) {
    return <ResourceError />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={t("meeting_minutes.list")}
        mainButton={{
          label: t("meeting_minutes.new"),
          path: "/dashboard/meeting-minutes/new",
          icon: PlusIcon,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("meeting_minutes.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {meetingMinutes.length === 0 ? (
          <ResourceEmpty message={t("meeting_minutes.no_results")} />
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {meetingMinutes.map((mm) => (
                <li key={mm.id}>
                  <div className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-6 w-6 text-gray-400 mr-3" />
                      <div>
                        <Link
                          to={`/dashboard/meeting-minutes/${mm.id}`}
                          className="text-lg font-medium text-indigo-600 hover:text-indigo-800"
                        >
                          {mm.title}
                        </Link>
                        <p className="text-sm text-gray-500 mt-1">
                          {mm.meeting_type} • {mm.meeting_date}
                          {mm.project && (
                            <span className="ml-2">
                              • <Link
                                to={`/dashboard/projects/${mm.project.id}`}
                                className="text-indigo-600 hover:text-indigo-800 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {mm.project.displayTitle || mm.project.name || `Project #${mm.project.id}`}
                              </Link>
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/dashboard/meeting-minutes/${mm.id}`}
                        className="text-gray-400 hover:text-gray-600"
                        title={t("meeting_minutes.view")}
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/dashboard/meeting-minutes/${mm.id}/edit`}
                        className="text-gray-400 hover:text-gray-600"
                        title={t("meeting_minutes.edit")}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/dashboard/meeting-minutes/new?copy_from=${mm.id}`}
                        className="text-gray-400 hover:text-gray-600"
                        title={t("meeting_minutes.templates.copy_from")}
                      >
                        <DocumentDuplicateIcon className="h-5 w-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(mm.id)}
                        className="text-red-400 hover:text-red-600"
                        title={t("meeting_minutes.delete")}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

